import { API_CONFIG } from '../config';
import { PRODUCTION_CONFIG } from '../config/production';

// Automatic failover between the home (Tailscale) backend and the AWS backend.
//
// How it works: every ApiService call reads `API_CONFIG.baseUrl` at request time,
// so we only need to mutate that single value at runtime. This module probes the
// home server's public health endpoint and points `API_CONFIG.baseUrl` at whichever
// backend is currently reachable, preferring home.
//
// Set the home URL at build time via REACT_APP_HOME_API_URL, e.g.
//   REACT_APP_HOME_API_URL=https://dellbox.tailnet-name.ts.net
// NOTE: when the frontend is served over HTTPS, the home URL must also be HTTPS
// (use `tailscale serve` to get a *.ts.net cert) or the browser blocks it as mixed
// content — in which case the probe simply fails and we stay on AWS.

const AWS_API_URL = PRODUCTION_CONFIG.API_URL;
const HOME_API_URL = process.env.REACT_APP_HOME_API_URL;
const PROBE_PATH = '/api/health';
const PROBE_TIMEOUT_MS = 1500;
const RECHECK_INTERVAL_MS = 30000;
const CACHE_KEY = 'eventreg_api_base';

async function isReachable(baseUrl) {
  if (!baseUrl) return false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`${baseUrl}${PROBE_PATH}`, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });
    return res.ok;
  } catch {
    // Timeout, network error, or mixed-content block -> treat as unreachable.
    return false;
  } finally {
    clearTimeout(timer);
  }
}

function setBaseUrl(url) {
  if (API_CONFIG.baseUrl === url) return;
  API_CONFIG.baseUrl = url;
  try {
    sessionStorage.setItem(CACHE_KEY, url);
  } catch {
    /* sessionStorage may be unavailable (private mode); non-fatal */
  }
  console.info(`[api] endpoint -> ${url}`);
}

// Prefer the home server when reachable, otherwise fall back to AWS.
// Safe to call repeatedly; returns the endpoint now in effect.
export async function refreshApiEndpoint() {
  if (!HOME_API_URL) return API_CONFIG.baseUrl;
  setBaseUrl((await isReachable(HOME_API_URL)) ? HOME_API_URL : AWS_API_URL);
  return API_CONFIG.baseUrl;
}

// Run once at app startup: pick the endpoint before the first request, then
// keep re-checking so the app can switch back to home when it comes online.
export async function initApiEndpoint() {
  if (!HOME_API_URL || typeof window === 'undefined') return;

  // Warm-start from the last session's choice to shave the first probe delay.
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached === HOME_API_URL || cached === AWS_API_URL) API_CONFIG.baseUrl = cached;
  } catch {
    /* ignore */
  }

  await refreshApiEndpoint();
  // ponytail: naive fixed-interval reprobe (no backoff). Upgrade path: exponential
  // backoff / pause when the tab is hidden if this ever gets chatty.
  setInterval(refreshApiEndpoint, RECHECK_INTERVAL_MS);
}
